import {
  ASSESSMENT_REPORT_SCHEMA,
  ASSESSMENT_REPORT_SCHEMA_VERSION,
  ASSESSMENT_REPORT_TEST_KEY,
  CBDF_CLASSIFICATION_VERSION,
  ICF_CLASSIFICATION_LANGUAGE,
  ICF_CLASSIFICATION_SOURCE_URI,
  ICF_CLASSIFICATION_VERSION,
  LEGACY_COMPOSITE_REPORT_TEST_KEY,
  getCbdfChapter,
  getIcfDomainByCode,
  isAssessmentReportAttachmentMode,
  isAssessmentReportAudienceType,
  isAssessmentReportDomain,
  isAssessmentReportFindingBasis,
  isAssessmentReportFindingStatus,
  isAssessmentReportHistoryMode,
  isAssessmentReportMode,
  isAssessmentReportNarrativeFormat,
  isAssessmentReportPurposeType,
  isAssessmentReportValidityStatus,
  isCbdfFullCodeForChapter,
  isCbdfChapterCode,
  isIcfCodeForDomain,
  isIcfDomain,
  isIcfFacilitatorQualifier,
  isIcfQualifierContext,
  isIcfProblemQualifier,
  normalizeIcfCode,
} from './assessmentReportCatalogs';
import type {
  AssessmentReportAttachmentMode,
  AssessmentReportAudienceType,
  AssessmentReportDomain,
  AssessmentReportFindingBasis,
  AssessmentReportFindingStatus,
  AssessmentReportHistoryMode,
  AssessmentReportMode,
  AssessmentReportNarrativeFormat,
  AssessmentReportPurposeType,
  AssessmentReportValidityStatus,
  CbdfChapterCode,
  IcfDomain,
  IcfFacilitatorQualifier,
  IcfProblemQualifier,
  IcfQualifier,
  IcfQualifierContext,
} from './assessmentReportCatalogs';
import {
  normalizeClinicalReferenceDecision,
  type ClinicalReferenceDecisionSnapshot,
} from '../references/clinicalReferenceRegistry';
import {
  captureClinicalAssessmentSnapshot,
  isAssessmentReportDocumentModel,
  normalizeAssessmentLongitudinalMeasure,
  type AssessmentLongitudinalMeasureSnapshot,
  type AssessmentReportDocumentModel,
} from './assessmentLongitudinalDomain';

export type { AssessmentReportDocumentModel } from './assessmentLongitudinalDomain';

export {
  getAssessmentReportDomainLabel,
  getAssessmentReportFindingStatusLabel,
  getAssessmentReportModeLabel,
  getAssessmentReportValidityStatusLabel,
} from './assessmentReportCatalogs';

export type AssessmentReportSourceLifecycleStatus =
  | 'draft'
  | 'completed'
  | 'cancelled'
  | 'unknown';

export interface AssessmentReportProfessionalSnapshotV2 {
  userId: string | null;
  name: string | null;
  registration: string | null;
  role: string | null;
}

export interface AssessmentReportPatientSnapshotV2 {
  patientId: string;
  name: string;
  medicalRecord: string | null;
  birthDate: string | null;
}

export interface AssessmentReportMeasureSnapshotV2 {
  key: string;
  label: string;
  rawValue: string | number | boolean | null;
  formattedValue: string;
  unit: string | null;
}

export interface AssessmentReportFindingSnapshotV2 {
  domain: AssessmentReportDomain;
  status: AssessmentReportFindingStatus;
  basis: AssessmentReportFindingBasis;
  label: string;
  conciseSummary: string | null;
  referenceLabel: string | null;
}

export interface AssessmentReportValiditySnapshotV2 {
  status: AssessmentReportValidityStatus;
  note: string | null;
  limitations: string[];
}

export interface AssessmentReportResultSnapshotV2 {
  testKey: string;
  testVersion: number;
  testLabel: string;
  assessmentDate: string;
  sourceStatus: AssessmentReportSourceLifecycleStatus;
  sourceCreatedAt: string | null;
  sourceUpdatedAt: string | null;
  capturedAt: string;
  primaryMeasure: AssessmentReportMeasureSnapshotV2;
  secondaryMeasures: AssessmentReportMeasureSnapshotV2[];
  interpretationText: string | null;
  referenceText: string | null;
  notes: string | null;
  longitudinalMeasures?: AssessmentLongitudinalMeasureSnapshot[];
  clinicalAssessment?: Record<string, unknown>;
}

/**
 * Fonte autocontida do relatório.
 *
 * `finding`, `validity`, `professional` e `snapshot` devem ser copiados de uma
 * avaliação existente ou registrados explicitamente pelo profissional. Este
 * contrato não autoriza derivar classificação clínica a partir do valor bruto.
 */
export interface AssessmentReportSourceSnapshotV2 {
  resultId: string;
  sessionId: string;
  finding: AssessmentReportFindingSnapshotV2;
  validity: AssessmentReportValiditySnapshotV2;
  professional: AssessmentReportProfessionalSnapshotV2;
  snapshot: AssessmentReportResultSnapshotV2;
  clinicalReference: ClinicalReferenceDecisionSnapshot | null;
}

export interface AssessmentReportAudienceContextV2 {
  type: AssessmentReportAudienceType;
  recipientName: string | null;
  recipientRole: string | null;
  organization: string | null;
  customLabel: string | null;
}

export interface AssessmentReportPurposeContextV2 {
  type: AssessmentReportPurposeType;
  description: string;
  referralQuestion: string | null;
}

export interface AssessmentReportAuthorshipV2 {
  origin: 'professional_authored';
  professional: AssessmentReportProfessionalSnapshotV2;
  authoredAt: string;
}

export interface AssessmentSummaryAuthoredContentV2 {
  mode: 'assessment_summary';
  origin: 'professional_authored';
  professionalOverview: string;
  followUpGuidance: string | null;
  observations: string[];
}

export interface PhysiotherapyTechnicalReportAuthoredContentV2 {
  mode: 'physiotherapy_technical_report';
  origin: 'professional_authored';
  clinicalContext: string;
  integratedAnalysis: string;
  physiotherapyConclusion: string;
  recommendations: string[];
  limitations: string | null;
}

export type AssessmentReportAuthoredContentV2 =
  | AssessmentSummaryAuthoredContentV2
  | PhysiotherapyTechnicalReportAuthoredContentV2;

export interface AssessmentReportHistoricalResultSnapshotV2 {
  resultId: string;
  sessionId: string;
  testKey: string;
  testVersion: number;
  assessmentDate: string;
  sourceStatus: AssessmentReportSourceLifecycleStatus;
  sourceCreatedAt: string | null;
  sourceUpdatedAt: string | null;
  capturedAt: string;
  primaryResult: string;
  classificationLabel: string | null;
  professional: AssessmentReportProfessionalSnapshotV2;
}

export interface AssessmentReportHistorySelectionV2 {
  sourceResultId: string;
  historicalResultIds: string[];
  /**
   * Snapshot explícito dos resultados históricos revisados.
   *
   * `historicalResultIds` é mantido para leitura dos primeiros payloads v2.
   * Para histórico ativo, a validação exige correspondência exata entre IDs e
   * snapshots e nunca os hidrata silenciosamente a partir dos dados atuais.
   */
  historicalResultSnapshots: AssessmentReportHistoricalResultSnapshotV2[];
}

export interface AssessmentReportExportOptionsV2 {
  narrativeFormat: AssessmentReportNarrativeFormat;
  longitudinalCharts?: boolean;
  attachments: {
    mode: AssessmentReportAttachmentMode;
    resultIds: string[];
  };
  history: {
    mode: AssessmentReportHistoryMode;
    selections: AssessmentReportHistorySelectionV2[];
  };
}

export interface AssessmentReportCbdfEntryV2 {
  id: string;
  chapterCode: CbdfChapterCode;
  /**
   * Código completo, digitado pelo profissional e preservado literalmente.
   * O sistema não completa subcódigos da CBDF.
   */
  manualCode: string | null;
  professionalStatement: string;
  rationale: string | null;
  evidenceResultIds: string[];
  origin: 'manual_professional_entry';
  recordedBy: AssessmentReportProfessionalSnapshotV2;
  recordedAt: string;
}

export interface AssessmentReportCbdfProfileV2 {
  classificationVersion: typeof CBDF_CLASSIFICATION_VERSION;
  origin: 'manual_professional_entry';
  entries: AssessmentReportCbdfEntryV2[];
  professionalNote: string | null;
}

type IcfCodeForDomain<D extends IcfDomain> =
  D extends 'body_functions'
    ? `b${number}`
    : D extends 'body_structures'
      ? `s${number}`
      : D extends 'activities_and_participation'
        ? `d${number}`
        : `e${number}`;

type IcfQualifierForDomain<D extends IcfDomain> =
  D extends 'environmental_factors'
    ? IcfProblemQualifier | IcfFacilitatorQualifier
    : IcfProblemQualifier;

export interface AssessmentReportIcfEntryV2<D extends IcfDomain = IcfDomain> {
  id: string;
  domain: D;
  code: IcfCodeForDomain<D>;
  qualifierContext: IcfQualifierContext;
  qualifier: IcfQualifierForDomain<D>;
  /**
   * Segundo qualificador de atividades e participação. Na notação CIF, o
   * desempenho ocupa a primeira posição e a capacidade, quando registrada,
   * ocupa a segunda (por exemplo, d450.12).
   */
  capacityQualifier: IcfProblemQualifier | null;
  professionalDescription: string;
  note: string | null;
  evidenceResultIds: string[];
  origin: 'manual_professional_entry';
  recordedBy: AssessmentReportProfessionalSnapshotV2;
  recordedAt: string;
}

export interface AssessmentReportIcfProfileV2 {
  classificationVersion: string;
  language: string;
  sourceUri: string;
  origin: 'manual_professional_entry';
  domains: {
    bodyFunctions: AssessmentReportIcfEntryV2<'body_functions'>[];
    bodyStructures: AssessmentReportIcfEntryV2<'body_structures'>[];
    activitiesAndParticipation: AssessmentReportIcfEntryV2<'activities_and_participation'>[];
    environmentalFactors: AssessmentReportIcfEntryV2<'environmental_factors'>[];
  };
  professionalSynthesis: string | null;
}

interface AssessmentReportEditorBase {
  mode: AssessmentReportMode;
  documentModel?: AssessmentReportDocumentModel;
  title: string;
  patient: AssessmentReportPatientSnapshotV2;
  audience: AssessmentReportAudienceContextV2;
  purpose: AssessmentReportPurposeContextV2;
  authorship: AssessmentReportAuthorshipV2;
  sources: AssessmentReportSourceSnapshotV2[];
  exportOptions: AssessmentReportExportOptionsV2;
  cbdf: AssessmentReportCbdfProfileV2;
  icf: AssessmentReportIcfProfileV2;
}

export type AssessmentReportEditorForm =
  | (AssessmentReportEditorBase & {
      mode: 'assessment_summary';
      content: AssessmentSummaryAuthoredContentV2;
    })
  | (AssessmentReportEditorBase & {
      mode: 'physiotherapy_technical_report';
      content: PhysiotherapyTechnicalReportAuthoredContentV2;
    });

interface AssessmentReportPayloadEnvelopeV2 {
  schema: typeof ASSESSMENT_REPORT_SCHEMA;
  schemaVersion: typeof ASSESSMENT_REPORT_SCHEMA_VERSION;
  testKey: typeof ASSESSMENT_REPORT_TEST_KEY;
  generatedAt: string;
  updatedAt: string;
}

export type AssessmentReportPayloadV2 =
  | (AssessmentReportPayloadEnvelopeV2 &
      AssessmentReportEditorBase & {
        mode: 'assessment_summary';
        content: AssessmentSummaryAuthoredContentV2;
      })
  | (AssessmentReportPayloadEnvelopeV2 &
      AssessmentReportEditorBase & {
        mode: 'physiotherapy_technical_report';
        content: PhysiotherapyTechnicalReportAuthoredContentV2;
      });

export interface LegacyCompositeReportPayload {
  title: string;
  clinicalSummary: string;
  mainFindings: string[];
  integratedInterpretation: string;
  attentionPoints: string[];
  limitations: string;
  finalText: string;
  sourceResultIds: string[];
}

export type AssessmentReportCompatibilityResult =
  | {
      kind: 'v2';
      readOnly: false;
      payload: AssessmentReportPayloadV2;
    }
  | {
      kind: 'legacy_composite_report';
      readOnly: true;
      payload: LegacyCompositeReportPayload;
    }
  | {
      kind: 'unsupported';
      readOnly: true;
      payload: null;
      reason: string;
    };

export type AssessmentReportValidationSeverity = 'error' | 'warning';

export interface AssessmentReportValidationIssue {
  path: string;
  code: string;
  message: string;
  severity: AssessmentReportValidationSeverity;
}

export interface AssessmentReportValidationResult {
  valid: boolean;
  issues: AssessmentReportValidationIssue[];
  errors: AssessmentReportValidationIssue[];
  warnings: AssessmentReportValidationIssue[];
}

export interface AssessmentReportFindingCounts {
  total: number;
  interpretable: number;
  withinExpected: number;
  borderline: number;
  altered: number;
  notClassified: number;
  notInterpretable: number;
  byStatus: Record<AssessmentReportFindingStatus, number>;
}

export interface AssessmentReportPeriod {
  startDate: string;
  endDate: string;
  isSingleDay: boolean;
  sourceCount: number;
  label: string;
}

export interface CreateInitialAssessmentReportFormOptions {
  mode?: AssessmentReportMode;
  documentModel?: AssessmentReportDocumentModel;
  patient?: Partial<AssessmentReportPatientSnapshotV2>;
  professional?: Partial<AssessmentReportProfessionalSnapshotV2>;
  now?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const requiredString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const nullableString = (value: unknown): string | null => {
  const normalized = requiredString(value);
  return normalized || null;
};

const stringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(requiredString).filter((item) => item.length > 0)
    : [];

const uniqueStrings = (values: readonly string[]): string[] => [...new Set(values)];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const ISO_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const isValidDateValue = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE_TIME_PATTERN.exec(value.trim());
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12) return false;

  const daysInMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1];
  if (day < 1 || day > daysInMonth) return false;

  if (match[4] === undefined) return true;
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const seconds = match[6] === undefined ? 0 : Number(match[6]);
  if (hours > 23 || minutes > 59 || seconds > 59) return false;

  const timezone = match[8];
  if (!timezone || timezone === 'Z') return true;
  const timezoneMatch = /^[+-](\d{2}):?(\d{2})$/.exec(timezone);
  if (!timezoneMatch) return false;
  const offsetHours = Number(timezoneMatch[1]);
  const offsetMinutes = Number(timezoneMatch[2]);
  return (
    offsetHours <= 14
    && offsetMinutes <= 59
    && (offsetHours < 14 || offsetMinutes === 0)
  );
};

const isSourceLifecycleStatus = (value: unknown): value is AssessmentReportSourceLifecycleStatus =>
  value === 'draft' || value === 'completed' || value === 'cancelled' || value === 'unknown';

const normalizeProfessionalSnapshot = (
  value: unknown,
): AssessmentReportProfessionalSnapshotV2 => {
  const source = isRecord(value) ? value : {};
  return {
    userId: nullableString(source.userId),
    name: nullableString(source.name),
    registration: nullableString(source.registration),
    role: nullableString(source.role),
  };
};

const normalizePatientSnapshot = (value: unknown): AssessmentReportPatientSnapshotV2 => {
  const source = isRecord(value) ? value : {};
  return {
    patientId: requiredString(source.patientId),
    name: requiredString(source.name),
    medicalRecord: nullableString(source.medicalRecord),
    birthDate: nullableString(source.birthDate),
  };
};

const normalizeMeasureSnapshot = (
  value: unknown,
): AssessmentReportMeasureSnapshotV2 | null => {
  if (!isRecord(value)) return null;
  const rawValue = value.rawValue;
  const validRawValue =
    rawValue === null
    || typeof rawValue === 'string'
    || typeof rawValue === 'boolean'
    || isFiniteNumber(rawValue);

  if (!validRawValue) return null;

  return {
    key: requiredString(value.key),
    label: requiredString(value.label),
    rawValue,
    formattedValue: requiredString(value.formattedValue),
    unit: nullableString(value.unit),
  };
};

const normalizeFindingSnapshot = (
  value: unknown,
): AssessmentReportFindingSnapshotV2 | null => {
  if (
    !isRecord(value)
    || !isAssessmentReportDomain(value.domain)
    || !isAssessmentReportFindingStatus(value.status)
    || !isAssessmentReportFindingBasis(value.basis)
  ) {
    return null;
  }

  return {
    domain: value.domain,
    status: value.status,
    basis: value.basis,
    label: requiredString(value.label),
    conciseSummary: nullableString(value.conciseSummary),
    referenceLabel: nullableString(value.referenceLabel),
  };
};

const normalizeValiditySnapshot = (
  value: unknown,
): AssessmentReportValiditySnapshotV2 | null => {
  if (!isRecord(value) || !isAssessmentReportValidityStatus(value.status)) return null;
  return {
    status: value.status,
    note: nullableString(value.note),
    limitations: stringArray(value.limitations),
  };
};

const normalizeResultSnapshot = (
  value: unknown,
): AssessmentReportResultSnapshotV2 | null => {
  if (!isRecord(value)) return null;

  const primaryMeasure = normalizeMeasureSnapshot(value.primaryMeasure);
  if (
    !primaryMeasure
    || !isFiniteNumber(value.testVersion)
    || !Number.isSafeInteger(value.testVersion)
    || value.testVersion < 1
    || !isSourceLifecycleStatus(value.sourceStatus)
  ) {
    return null;
  }

  const rawSecondaryMeasures = Array.isArray(value.secondaryMeasures)
    ? value.secondaryMeasures
    : [];
  const secondaryMeasures = rawSecondaryMeasures.map(normalizeMeasureSnapshot);
  if (secondaryMeasures.some((measure) => measure === null)) return null;
  const longitudinalMeasures = Array.isArray(value.longitudinalMeasures)
    ? value.longitudinalMeasures.map(normalizeAssessmentLongitudinalMeasure)
    : [];
  if (longitudinalMeasures.some((measure) => measure === null)) return null;
  const clinicalAssessment = captureClinicalAssessmentSnapshot(value.clinicalAssessment);

  return {
    testKey: requiredString(value.testKey),
    testVersion: value.testVersion,
    testLabel: requiredString(value.testLabel),
    assessmentDate: requiredString(value.assessmentDate),
    sourceStatus: value.sourceStatus,
    sourceCreatedAt: nullableString(value.sourceCreatedAt),
    sourceUpdatedAt: nullableString(value.sourceUpdatedAt),
    capturedAt: requiredString(value.capturedAt),
    primaryMeasure,
    secondaryMeasures: secondaryMeasures as AssessmentReportMeasureSnapshotV2[],
    interpretationText: nullableString(value.interpretationText),
    referenceText: nullableString(value.referenceText),
    notes: nullableString(value.notes),
    ...(Array.isArray(value.longitudinalMeasures)
      ? { longitudinalMeasures: longitudinalMeasures as AssessmentLongitudinalMeasureSnapshot[] }
      : {}),
    ...(clinicalAssessment ? { clinicalAssessment } : {}),
  };
};

export const normalizeAssessmentReportSourceSnapshot = (
  value: unknown,
): AssessmentReportSourceSnapshotV2 | null => {
  if (!isRecord(value)) return null;

  const finding = normalizeFindingSnapshot(value.finding);
  const validity = normalizeValiditySnapshot(value.validity);
  const snapshot = normalizeResultSnapshot(value.snapshot);
  if (!finding || !validity || !snapshot) return null;

  return {
    resultId: requiredString(value.resultId),
    sessionId: requiredString(value.sessionId),
    finding,
    validity,
    professional: normalizeProfessionalSnapshot(value.professional),
    snapshot,
    clinicalReference: normalizeClinicalReferenceDecision(value.clinicalReference),
  };
};

const normalizeAudienceContext = (
  value: unknown,
): AssessmentReportAudienceContextV2 | null => {
  if (!isRecord(value) || !isAssessmentReportAudienceType(value.type)) return null;
  return {
    type: value.type,
    recipientName: nullableString(value.recipientName),
    recipientRole: nullableString(value.recipientRole),
    organization: nullableString(value.organization),
    customLabel: nullableString(value.customLabel),
  };
};

const normalizePurposeContext = (
  value: unknown,
): AssessmentReportPurposeContextV2 | null => {
  if (!isRecord(value) || !isAssessmentReportPurposeType(value.type)) return null;
  return {
    type: value.type,
    description: requiredString(value.description),
    referralQuestion: nullableString(value.referralQuestion),
  };
};

const normalizeAuthorship = (value: unknown): AssessmentReportAuthorshipV2 | null => {
  if (!isRecord(value) || value.origin !== 'professional_authored') return null;
  return {
    origin: 'professional_authored',
    professional: normalizeProfessionalSnapshot(value.professional),
    authoredAt: requiredString(value.authoredAt),
  };
};

const normalizeAuthoredContent = (
  value: unknown,
  mode: AssessmentReportMode,
): AssessmentReportAuthoredContentV2 | null => {
  if (
    !isRecord(value)
    || value.mode !== mode
    || value.origin !== 'professional_authored'
  ) {
    return null;
  }

  if (mode === 'assessment_summary') {
    return {
      mode,
      origin: 'professional_authored',
      professionalOverview: requiredString(value.professionalOverview),
      followUpGuidance: nullableString(value.followUpGuidance),
      observations: stringArray(value.observations),
    };
  }

  return {
    mode,
    origin: 'professional_authored',
    clinicalContext: requiredString(value.clinicalContext),
    integratedAnalysis: requiredString(value.integratedAnalysis),
    physiotherapyConclusion: requiredString(value.physiotherapyConclusion),
    recommendations: stringArray(value.recommendations),
    limitations: nullableString(value.limitations),
  };
};

const normalizeHistoricalResultSnapshot = (
  value: unknown,
): AssessmentReportHistoricalResultSnapshotV2 | null => {
  if (
    !isRecord(value)
    || !isFiniteNumber(value.testVersion)
    || !Number.isSafeInteger(value.testVersion)
    || value.testVersion < 1
    || !isSourceLifecycleStatus(value.sourceStatus)
  ) {
    return null;
  }

  return {
    resultId: requiredString(value.resultId),
    sessionId: requiredString(value.sessionId),
    testKey: requiredString(value.testKey),
    testVersion: value.testVersion,
    assessmentDate: requiredString(value.assessmentDate),
    sourceStatus: value.sourceStatus,
    sourceCreatedAt: nullableString(value.sourceCreatedAt),
    sourceUpdatedAt: nullableString(value.sourceUpdatedAt),
    capturedAt: requiredString(value.capturedAt),
    primaryResult: requiredString(value.primaryResult),
    classificationLabel: nullableString(value.classificationLabel),
    professional: normalizeProfessionalSnapshot(value.professional),
  };
};

const normalizeExportOptions = (
  value: unknown,
): AssessmentReportExportOptionsV2 | null => {
  if (!isRecord(value) || !isRecord(value.attachments) || !isRecord(value.history)) {
    return null;
  }

  if (
    !isAssessmentReportAttachmentMode(value.attachments.mode)
    || !isAssessmentReportHistoryMode(value.history.mode)
  ) {
    return null;
  }
  const narrativeFormat =
    value.narrativeFormat === undefined
      ? 'sections'
      : isAssessmentReportNarrativeFormat(value.narrativeFormat)
        ? value.narrativeFormat
        : null;
  if (!narrativeFormat) return null;
  if (value.longitudinalCharts !== undefined && typeof value.longitudinalCharts !== 'boolean') return null;

  const rawSelections = Array.isArray(value.history.selections)
    ? value.history.selections
    : [];
  const selections = rawSelections.map((item) => {
    if (!isRecord(item)) return null;

    const rawSnapshots =
      item.historicalResultSnapshots === undefined
        ? []
        : item.historicalResultSnapshots;
    if (!Array.isArray(rawSnapshots)) return null;
    const historicalResultSnapshots = rawSnapshots.map(normalizeHistoricalResultSnapshot);
    if (historicalResultSnapshots.some((snapshot) => snapshot === null)) return null;

    return {
      sourceResultId: requiredString(item.sourceResultId),
      historicalResultIds: uniqueStrings(stringArray(item.historicalResultIds)),
      historicalResultSnapshots:
        historicalResultSnapshots as AssessmentReportHistoricalResultSnapshotV2[],
    };
  });
  if (selections.some((selection) => selection === null)) return null;

  return {
    narrativeFormat,
    longitudinalCharts: value.longitudinalCharts !== false,
    attachments: {
      mode: value.attachments.mode,
      resultIds: uniqueStrings(stringArray(value.attachments.resultIds)),
    },
    history: {
      mode: value.history.mode,
      selections: selections as AssessmentReportHistorySelectionV2[],
    },
  };
};

const normalizeCbdfEntry = (value: unknown): AssessmentReportCbdfEntryV2 | null => {
  if (
    !isRecord(value)
    || value.origin !== 'manual_professional_entry'
    || !isCbdfChapterCode(value.chapterCode)
  ) {
    return null;
  }

  return {
    id: requiredString(value.id),
    chapterCode: value.chapterCode,
    manualCode: nullableString(value.manualCode),
    professionalStatement: requiredString(value.professionalStatement),
    rationale: nullableString(value.rationale),
    evidenceResultIds: uniqueStrings(stringArray(value.evidenceResultIds)),
    origin: 'manual_professional_entry',
    recordedBy: normalizeProfessionalSnapshot(value.recordedBy),
    recordedAt: requiredString(value.recordedAt),
  };
};

const normalizeCbdfProfile = (
  value: unknown,
): AssessmentReportCbdfProfileV2 | null => {
  if (
    !isRecord(value)
    || value.classificationVersion !== CBDF_CLASSIFICATION_VERSION
    || value.origin !== 'manual_professional_entry'
  ) {
    return null;
  }
  const rawEntries = Array.isArray(value.entries) ? value.entries : [];
  const entries = rawEntries.map(normalizeCbdfEntry);
  if (entries.some((entry) => entry === null)) return null;

  return {
    classificationVersion: CBDF_CLASSIFICATION_VERSION,
    origin: 'manual_professional_entry',
    entries: entries as AssessmentReportCbdfEntryV2[],
    professionalNote: nullableString(value.professionalNote),
  };
};

const normalizeIcfEntry = <D extends IcfDomain>(
  value: unknown,
  expectedDomain: D,
): AssessmentReportIcfEntryV2<D> | null => {
  if (
    !isRecord(value)
    || value.origin !== 'manual_professional_entry'
    || value.domain !== expectedDomain
    || !isIcfCodeForDomain(value.code, expectedDomain)
  ) {
    return null;
  }

  const qualifierIsValid =
    expectedDomain === 'environmental_factors'
      ? isIcfProblemQualifier(value.qualifier) || isIcfFacilitatorQualifier(value.qualifier)
      : isIcfProblemQualifier(value.qualifier);
  if (!qualifierIsValid) return null;
  const capacityQualifier =
    value.capacityQualifier === undefined || value.capacityQualifier === null
      ? null
      : isIcfProblemQualifier(value.capacityQualifier)
        ? value.capacityQualifier
        : undefined;
  if (
    capacityQualifier === undefined
    || (expectedDomain !== 'activities_and_participation' && capacityQualifier !== null)
  ) {
    return null;
  }
  const qualifierContext =
    value.qualifierContext === undefined
      ? 'extent'
      : isIcfQualifierContext(value.qualifierContext)
        ? value.qualifierContext
        : null;
  if (!qualifierContext) return null;

  return {
    id: requiredString(value.id),
    domain: expectedDomain,
    code: normalizeIcfCode(value.code as string) as IcfCodeForDomain<D>,
    qualifierContext,
    qualifier: value.qualifier as IcfQualifierForDomain<D>,
    capacityQualifier,
    professionalDescription: requiredString(value.professionalDescription),
    note: nullableString(value.note),
    evidenceResultIds: uniqueStrings(stringArray(value.evidenceResultIds)),
    origin: 'manual_professional_entry',
    recordedBy: normalizeProfessionalSnapshot(value.recordedBy),
    recordedAt: requiredString(value.recordedAt),
  };
};

const normalizeIcfEntryArray = <D extends IcfDomain>(
  value: unknown,
  domain: D,
): AssessmentReportIcfEntryV2<D>[] | null => {
  if (!Array.isArray(value)) return null;
  const entries = value.map((entry) => normalizeIcfEntry(entry, domain));
  return entries.some((entry) => entry === null)
    ? null
    : entries as AssessmentReportIcfEntryV2<D>[];
};

const normalizeIcfProfile = (value: unknown): AssessmentReportIcfProfileV2 | null => {
  if (
    !isRecord(value)
    || value.origin !== 'manual_professional_entry'
    || !isRecord(value.domains)
  ) {
    return null;
  }

  const bodyFunctions = normalizeIcfEntryArray(
    value.domains.bodyFunctions,
    'body_functions',
  );
  const bodyStructures = normalizeIcfEntryArray(
    value.domains.bodyStructures,
    'body_structures',
  );
  const activitiesAndParticipation = normalizeIcfEntryArray(
    value.domains.activitiesAndParticipation,
    'activities_and_participation',
  );
  const environmentalFactors = normalizeIcfEntryArray(
    value.domains.environmentalFactors,
    'environmental_factors',
  );

  if (
    !bodyFunctions
    || !bodyStructures
    || !activitiesAndParticipation
    || !environmentalFactors
  ) {
    return null;
  }

  return {
    classificationVersion:
      value.classificationVersion === undefined
        ? ICF_CLASSIFICATION_VERSION
        : requiredString(value.classificationVersion),
    language:
      value.language === undefined
        ? ICF_CLASSIFICATION_LANGUAGE
        : requiredString(value.language),
    sourceUri:
      value.sourceUri === undefined
        ? ICF_CLASSIFICATION_SOURCE_URI
        : requiredString(value.sourceUri),
    origin: 'manual_professional_entry',
    domains: {
      bodyFunctions,
      bodyStructures,
      activitiesAndParticipation,
      environmentalFactors,
    },
    professionalSynthesis: nullableString(value.professionalSynthesis),
  };
};

export const createEmptyAssessmentReportIcfProfile =
  (): AssessmentReportIcfProfileV2 => ({
    classificationVersion: ICF_CLASSIFICATION_VERSION,
    language: ICF_CLASSIFICATION_LANGUAGE,
    sourceUri: ICF_CLASSIFICATION_SOURCE_URI,
    origin: 'manual_professional_entry',
    domains: {
      bodyFunctions: [],
      bodyStructures: [],
      activitiesAndParticipation: [],
      environmentalFactors: [],
    },
    professionalSynthesis: null,
  });

export const createInitialAssessmentReportForm = (
  modeOrOptions: AssessmentReportMode | CreateInitialAssessmentReportFormOptions =
    'assessment_summary',
): AssessmentReportEditorForm => {
  const options =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions }
      : modeOrOptions;
  const mode = options.mode ?? 'assessment_summary';
  const now = isValidDateValue(options.now)
    ? options.now
    : new Date().toISOString();
  const professional = normalizeProfessionalSnapshot(options.professional);
  const base = {
    documentModel: options.documentModel ?? (mode === 'assessment_summary' ? 'summary' : 'findings'),
    title:
      mode === 'assessment_summary'
        ? 'Resumo das avaliações'
        : 'Relatório técnico com diagnóstico fisioterapêutico',
    patient: normalizePatientSnapshot(options.patient),
    audience: {
      type: 'patient_or_caregiver' as const,
      recipientName: null,
      recipientRole: null,
      organization: null,
      customLabel: null,
    },
    purpose: {
      type: 'patient_feedback' as const,
      description: '',
      referralQuestion: null,
    },
    authorship: {
      origin: 'professional_authored' as const,
      professional,
      authoredAt: now,
    },
    sources: [],
    exportOptions: {
      narrativeFormat: 'sections' as const,
      longitudinalCharts: true,
      attachments: {
        mode: 'cover_only' as const,
        resultIds: [],
      },
      history: {
        mode: 'none' as const,
        selections: [],
      },
    },
    cbdf: {
      classificationVersion: CBDF_CLASSIFICATION_VERSION,
      origin: 'manual_professional_entry' as const,
      entries: [],
      professionalNote: null,
    },
    icf: createEmptyAssessmentReportIcfProfile(),
  };

  if (mode === 'assessment_summary') {
    return {
      ...base,
      mode,
      content: {
        mode,
        origin: 'professional_authored',
        professionalOverview: '',
        followUpGuidance: null,
        observations: [],
      },
    };
  }

  return {
    ...base,
    mode,
    content: {
      mode,
      origin: 'professional_authored',
      clinicalContext: '',
      integratedAnalysis: '',
      physiotherapyConclusion: '',
      recommendations: [],
      limitations: null,
    },
  };
};

export const normalizeAssessmentReportEditorForm = (
  value: unknown,
): AssessmentReportEditorForm | null => {
  if (!isRecord(value) || !isAssessmentReportMode(value.mode)) return null;

  const mode = value.mode;
  if (value.documentModel !== undefined && !isAssessmentReportDocumentModel(value.documentModel)) return null;
  const audience = normalizeAudienceContext(value.audience);
  const purpose = normalizePurposeContext(value.purpose);
  const authorship = normalizeAuthorship(value.authorship);
  const content = normalizeAuthoredContent(value.content, mode);
  const exportOptions = normalizeExportOptions(value.exportOptions);
  const cbdf = normalizeCbdfProfile(value.cbdf);
  const icf = normalizeIcfProfile(value.icf);
  const rawSources = Array.isArray(value.sources) ? value.sources : null;
  if (
    !audience
    || !purpose
    || !authorship
    || !content
    || !exportOptions
    || !cbdf
    || !icf
    || !rawSources
  ) {
    return null;
  }

  const sources = rawSources.map(normalizeAssessmentReportSourceSnapshot);
  if (sources.some((source) => source === null)) return null;

  const base = {
    documentModel: value.documentModel ?? (mode === 'assessment_summary' ? 'summary' : 'findings'),
    title: requiredString(value.title),
    patient: normalizePatientSnapshot(value.patient),
    audience,
    purpose,
    authorship,
    sources: sources as AssessmentReportSourceSnapshotV2[],
    exportOptions,
    cbdf,
    icf,
  };

  if (mode === 'assessment_summary' && content.mode === mode) {
    return { ...base, mode, content };
  }
  if (mode === 'physiotherapy_technical_report' && content.mode === mode) {
    return { ...base, mode, content };
  }
  return null;
};

export const normalizeAssessmentReportPayload = (
  value: unknown,
): AssessmentReportPayloadV2 | null => {
  if (
    !isRecord(value)
    || value.schema !== ASSESSMENT_REPORT_SCHEMA
    || value.schemaVersion !== ASSESSMENT_REPORT_SCHEMA_VERSION
    || value.testKey !== ASSESSMENT_REPORT_TEST_KEY
  ) {
    return null;
  }

  const form = normalizeAssessmentReportEditorForm(value);
  if (!form) return null;

  const envelope: AssessmentReportPayloadEnvelopeV2 = {
    schema: ASSESSMENT_REPORT_SCHEMA,
    schemaVersion: ASSESSMENT_REPORT_SCHEMA_VERSION,
    testKey: ASSESSMENT_REPORT_TEST_KEY,
    generatedAt: requiredString(value.generatedAt),
    updatedAt: requiredString(value.updatedAt),
  };

  if (form.mode === 'assessment_summary') {
    return { ...envelope, ...form };
  }
  return { ...envelope, ...form };
};

export const applyAssessmentReportDescriptiveFallback = (
  form: AssessmentReportEditorForm,
): AssessmentReportEditorForm => ({
  ...form,
  sources: form.sources.map((source) => {
    const interpreted = source.finding.status === 'within_expected'
      || source.finding.status === 'borderline'
      || source.finding.status === 'altered';
    if (!interpreted || source.finding.referenceLabel?.trim()) return source;

    return {
      ...source,
      finding: {
        ...source.finding,
        status: 'not_classified',
        basis: 'descriptive_only',
      },
    };
  }),
});

export const createAssessmentReportPayloadV2 = (
  form: AssessmentReportEditorForm,
  timestamps?: { generatedAt?: string; updatedAt?: string },
): AssessmentReportPayloadV2 => {
  const safeForm = applyAssessmentReportDescriptiveFallback(form);
  const generatedAt = isValidDateValue(timestamps?.generatedAt)
    ? timestamps.generatedAt
    : new Date().toISOString();
  const updatedAt = isValidDateValue(timestamps?.updatedAt)
    ? timestamps.updatedAt
    : generatedAt;

  if (safeForm.mode === 'assessment_summary') {
    return {
      ...safeForm,
      schema: ASSESSMENT_REPORT_SCHEMA,
      schemaVersion: ASSESSMENT_REPORT_SCHEMA_VERSION,
      testKey: ASSESSMENT_REPORT_TEST_KEY,
      generatedAt,
      updatedAt,
    };
  }

  return {
    ...safeForm,
    schema: ASSESSMENT_REPORT_SCHEMA,
    schemaVersion: ASSESSMENT_REPORT_SCHEMA_VERSION,
    testKey: ASSESSMENT_REPORT_TEST_KEY,
    generatedAt,
    updatedAt,
  };
};

export const isAssessmentReportSourceSnapshotV2 = (
  value: unknown,
): value is AssessmentReportSourceSnapshotV2 =>
  normalizeAssessmentReportSourceSnapshot(value) !== null;

export const isAssessmentReportEditorForm = (
  value: unknown,
): value is AssessmentReportEditorForm =>
  normalizeAssessmentReportEditorForm(value) !== null;

export const isAssessmentReportPayloadV2 = (
  value: unknown,
): value is AssessmentReportPayloadV2 =>
  normalizeAssessmentReportPayload(value) !== null;

const normalizeLegacyList = (value: unknown): string[] => {
  if (Array.isArray(value)) return stringArray(value);
  if (typeof value !== 'string') return [];
  return value
    .split(/\r?\n/)
    .map((item) => item.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter(Boolean);
};

export const normalizeLegacyCompositeReportPayload = (
  value: unknown,
): LegacyCompositeReportPayload | null => {
  if (!isRecord(value)) return null;

  const sourceResultIds = [
    ...stringArray(value.sourceResultIds),
    ...(Array.isArray(value.sourceResults)
      ? value.sourceResults
          .map((item) => isRecord(item) ? requiredString(item.resultId) : '')
          .filter(Boolean)
      : []),
  ];
  const hasLegacyField =
    'clinicalSummary' in value
    || 'integratedInterpretation' in value
    || 'mainFindings' in value
    || 'mainFindingsText' in value
    || 'sourceResults' in value;
  if (!hasLegacyField) return null;

  return {
    title: requiredString(value.title) || 'Relatório de avaliações — modelo anterior',
    clinicalSummary: requiredString(value.clinicalSummary),
    mainFindings: normalizeLegacyList(value.mainFindings ?? value.mainFindingsText),
    integratedInterpretation: requiredString(value.integratedInterpretation),
    attentionPoints: normalizeLegacyList(value.attentionPoints ?? value.attentionPointsText),
    limitations: requiredString(value.limitations),
    finalText: requiredString(value.finalText),
    sourceResultIds: uniqueStrings(sourceResultIds),
  };
};

/**
 * Lê o modelo anterior sem convertê-lo silenciosamente para v2.
 *
 * Achados, CBDF e CIF antigos permanecem somente para leitura, pois o contrato
 * anterior não registra base, validade e autoria suficientes para uma migração
 * clínica segura.
 */
export const normalizeAssessmentReportCompatibility = (
  value: unknown,
  testKey?: string,
): AssessmentReportCompatibilityResult => {
  if (!testKey || testKey === ASSESSMENT_REPORT_TEST_KEY) {
    const payload = normalizeAssessmentReportPayload(value);
    if (payload) return { kind: 'v2', readOnly: false, payload };
    if (testKey === ASSESSMENT_REPORT_TEST_KEY) {
      return {
        kind: 'unsupported',
        readOnly: true,
        payload: null,
        reason: 'Payload ASSESSMENT_REPORT ausente, inválido ou de versão não suportada.',
      };
    }
  }

  if (!testKey || testKey === LEGACY_COMPOSITE_REPORT_TEST_KEY) {
    const legacyPayload = normalizeLegacyCompositeReportPayload(value);
    if (legacyPayload) {
      return {
        kind: 'legacy_composite_report',
        readOnly: true,
        payload: legacyPayload,
      };
    }
  }

  return {
    kind: 'unsupported',
    readOnly: true,
    payload: null,
    reason: 'O conteúdo não corresponde a um relatório suportado.',
  };
};

const makeValidationResult = (
  issues: AssessmentReportValidationIssue[],
): AssessmentReportValidationResult => {
  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');
  return {
    valid: errors.length === 0,
    issues,
    errors,
    warnings,
  };
};

const validateEditorForm = (
  form: AssessmentReportEditorForm,
): AssessmentReportValidationIssue[] => {
  const issues: AssessmentReportValidationIssue[] = [];
  const add = (
    path: string,
    code: string,
    message: string,
    severity: AssessmentReportValidationSeverity = 'error',
  ) => issues.push({ path, code, message, severity });

  if (!form.title) add('title', 'required', 'Informe o título do relatório.');
  if (form.documentModel === 'findings' && form.mode !== 'physiotherapy_technical_report') {
    add('documentModel', 'technical_model_requires_technical_mode',
      'O modelo de achados com diagnóstico fisioterapêutico exige o modo técnico com CBDF e CIF.');
  }
  if (form.icf.classificationVersion !== ICF_CLASSIFICATION_VERSION) {
    add(
      'icf.classificationVersion',
      'unsupported_icf_release',
      `Use a edição CIF ${ICF_CLASSIFICATION_VERSION}.`,
    );
  }
  if (form.icf.language !== ICF_CLASSIFICATION_LANGUAGE) {
    add(
      'icf.language',
      'unsupported_icf_language',
      `Use a versão CIF em ${ICF_CLASSIFICATION_LANGUAGE}.`,
    );
  }
  if (form.icf.sourceUri !== ICF_CLASSIFICATION_SOURCE_URI) {
    add(
      'icf.sourceUri',
      'invalid_icf_source',
      'Use a fonte oficial correspondente à edição CIF adotada.',
    );
  }
  if (!form.patient.patientId) {
    add('patient.patientId', 'required', 'O relatório deve identificar o paciente.');
  }
  if (!form.patient.name) {
    add('patient.name', 'required', 'Informe o nome exibido do paciente.');
  }
  if (form.patient.birthDate && !isValidDateValue(form.patient.birthDate)) {
    add('patient.birthDate', 'invalid_date', 'A data de nascimento é inválida.');
  }
  if (form.audience.type === 'other' && !form.audience.customLabel) {
    add('audience.customLabel', 'required_for_other', 'Descreva o destinatário.');
  }
  if (form.purpose.type === 'other' && !form.purpose.description) {
    add('purpose.description', 'required', 'Descreva a finalidade deste relatório.');
  }
  if (form.purpose.type === 'referral' && !form.purpose.referralQuestion) {
    add(
      'purpose.referralQuestion',
      'recommended_for_referral',
      'Informe a pergunta ou demanda do encaminhamento.',
      'warning',
    );
  }
  if (!form.authorship.professional.name) {
    add(
      'authorship.professional.name',
      'required',
      'Identifique o profissional autor do conteúdo.',
    );
  }
  if (!isValidDateValue(form.authorship.authoredAt)) {
    add('authorship.authoredAt', 'invalid_date', 'A data de autoria é inválida.');
  }
  if (form.sources.length === 0) {
    add('sources', 'minimum_sources', 'Selecione ao menos uma avaliação concluída.');
  }

  const sourceIds = new Set<string>();
  const sourceById = new Map<string, AssessmentReportSourceSnapshotV2>();
  form.sources.forEach((source, index) => {
    const path = `sources.${index}`;
    if (!source.resultId) add(`${path}.resultId`, 'required', 'A fonte deve ter um identificador.');
    if (!source.sessionId) add(`${path}.sessionId`, 'required', 'A fonte deve identificar a sessão de origem.');
    if (sourceIds.has(source.resultId)) {
      add(`${path}.resultId`, 'duplicate_source', 'A avaliação-fonte está repetida.');
    }
    sourceIds.add(source.resultId);
    sourceById.set(source.resultId, source);
    if (!source.snapshot.testKey) add(`${path}.snapshot.testKey`, 'required', 'Informe o instrumento.');
    if (
      source.snapshot.testKey === ASSESSMENT_REPORT_TEST_KEY
      || source.snapshot.testKey === LEGACY_COMPOSITE_REPORT_TEST_KEY
    ) {
      add(
        `${path}.snapshot.testKey`,
        'nested_report_source',
        'Um relatório não pode ser usado como avaliação-fonte de outro relatório.',
      );
    }
    if (!source.snapshot.testLabel) add(`${path}.snapshot.testLabel`, 'required', 'Informe o nome do instrumento.');
    if (source.snapshot.sourceStatus !== 'completed') {
      add(
        `${path}.snapshot.sourceStatus`,
        'source_not_completed',
        'Somente avaliações concluídas podem compor o relatório.',
      );
    }
    if (!isValidDateValue(source.snapshot.assessmentDate)) {
      add(`${path}.snapshot.assessmentDate`, 'invalid_date', 'A data da avaliação é inválida.');
    }
    if (!isValidDateValue(source.snapshot.capturedAt)) {
      add(`${path}.snapshot.capturedAt`, 'invalid_date', 'A data do snapshot é inválida.');
    }
    if (!source.snapshot.primaryMeasure.label || !source.snapshot.primaryMeasure.formattedValue) {
      add(
        `${path}.snapshot.primaryMeasure`,
        'incomplete_measure',
        'A medida principal precisa de rótulo e valor formatado.',
      );
    }
    if (
      source.snapshot.sourceCreatedAt
      && !isValidDateValue(source.snapshot.sourceCreatedAt)
    ) {
      add(
        `${path}.snapshot.sourceCreatedAt`,
        'invalid_date',
        'A data de criação da avaliação-fonte é inválida.',
      );
    }
    if (
      source.snapshot.sourceUpdatedAt
      && !isValidDateValue(source.snapshot.sourceUpdatedAt)
    ) {
      add(
        `${path}.snapshot.sourceUpdatedAt`,
        'invalid_date',
        'A data de atualização da avaliação-fonte é inválida.',
      );
    }
    if (!source.finding.label) {
      add(`${path}.finding.label`, 'required', 'Informe o rótulo objetivo do achado.');
    }
    const findingIsInterpretable =
      source.finding.status === 'within_expected'
      || source.finding.status === 'borderline'
      || source.finding.status === 'altered';
    if (
      findingIsInterpretable
      && source.finding.basis === 'descriptive_only'
    ) {
      add(
        `${path}.finding.basis`,
        'interpretable_finding_without_basis',
        'Um achado classificado deve indicar referência do instrumento ou revisão profissional.',
      );
    }
    if (
      !findingIsInterpretable
      && source.finding.basis !== 'descriptive_only'
    ) {
      add(
        `${path}.finding.basis`,
        'descriptive_finding_with_interpretive_basis',
        'Resultados sem classificação ou não interpretáveis devem ser apresentados sem classificação clínica.',
      );
    }
    if (
      findingIsInterpretable
      && !source.finding.referenceLabel
    ) {
      add(
        `${path}.finding.referenceLabel`,
        'finding_reference_required',
        'Informe o critério ou a referência usados para sustentar esta situação.',
      );
    }
    if (
      source.validity.status === 'invalid'
      && findingIsInterpretable
    ) {
      add(
        `${path}.finding.status`,
        'invalid_source_interpretation',
        'Revise o resultado na avaliação de origem antes de atribuir uma classificação clínica.',
      );
    }
    if (!source.professional.name) {
      add(
        `${path}.professional.name`,
        'missing_source_professional',
        'A avaliação-fonte não identifica o profissional responsável.',
        'warning',
      );
    }
  });

  const icfEntryCount =
    form.icf.domains.bodyFunctions.length
    + form.icf.domains.bodyStructures.length
    + form.icf.domains.activitiesAndParticipation.length
    + form.icf.domains.environmentalFactors.length;

  if (form.mode === 'assessment_summary') {
    if (
      form.cbdf.entries.length > 0
      || form.cbdf.professionalNote
      || icfEntryCount > 0
      || form.icf.professionalSynthesis
    ) {
      add(
        'mode',
        'technical_content_in_summary',
        'CBDF e CIF pertencem ao relatório técnico e não podem ficar ocultas no resumo.',
      );
    }
  } else {
    if (!form.authorship.professional.userId) {
      add(
        'authorship.professional.userId',
        'professional_user_required',
        'O relatório técnico deve identificar a conta do fisioterapeuta autor.',
      );
    }
    if (form.authorship.professional.role !== 'fisioterapeuta') {
      add(
        'authorship.professional.role',
        'physiotherapist_required',
        'O relatório técnico com diagnóstico fisioterapêutico deve ser emitido por fisioterapeuta.',
      );
    }
    if (!form.authorship.professional.registration) {
      add(
        'authorship.professional.registration',
        'professional_registration_required',
        'Informe o registro profissional (CREFITO).',
      );
    }
    if (!form.content.clinicalContext) {
      add('content.clinicalContext', 'required', 'Descreva o contexto clínico-funcional.');
    }
    if (!form.content.integratedAnalysis) {
      add('content.integratedAnalysis', 'required', 'Registre a análise integrada.');
    }
    if (!form.content.physiotherapyConclusion) {
      add(
        'content.physiotherapyConclusion',
        'required',
        'Registre a conclusão fisioterapêutica autoral.',
      );
    } else if (/\[\s*complemente\b/i.test(form.content.physiotherapyConclusion)) {
      add(
        'content.physiotherapyConclusion',
        'assisted_conclusion_requires_completion',
        'Complete o trecho sinalizado na minuta antes de salvar a conclusão fisioterapêutica.',
      );
    }
    if (form.cbdf.entries.length === 0) {
      add(
        'cbdf.entries',
        'cbdf_required',
        'Por regra clínica deste modo no RevaHub, inclua ao menos um diagnóstico fisioterapêutico segundo a CBDF-1.',
      );
    }
    if (icfEntryCount === 0) {
      add(
        'icf.domains',
        'icf_profile_required',
        'Por regra clínica deste modo no RevaHub, inclua ao menos um item no perfil funcional segundo a CIF.',
      );
    }
  }

  const attachmentIds = form.exportOptions.attachments.resultIds;
  if (
    form.exportOptions.attachments.mode === 'selected_assessments'
    && attachmentIds.length === 0
  ) {
    add(
      'exportOptions.attachments.resultIds',
      'required_for_selected_attachments',
      'Selecione as avaliações que serão anexadas.',
    );
  }
  attachmentIds.forEach((resultId, index) => {
    if (!sourceIds.has(resultId)) {
      add(
        `exportOptions.attachments.resultIds.${index}`,
        'attachment_not_in_sources',
        'O anexo deve corresponder a uma avaliação-fonte.',
      );
    }
  });
  if (
    form.exportOptions.attachments.mode !== 'selected_assessments'
    && attachmentIds.length > 0
  ) {
    add(
      'exportOptions.attachments.resultIds',
      'unused_attachment_selection',
      'Há anexos selecionados que não serão usados pelo modo atual.',
      'warning',
    );
  }

  const history = form.exportOptions.history;
  if (history.mode === 'selected' && history.selections.length === 0) {
    add(
      'exportOptions.history.selections',
      'required_for_history',
      'Selecione ao menos uma comparação histórica.',
    );
  }
  if (
    history.mode === 'selected'
    && form.exportOptions.attachments.mode === 'cover_only'
  ) {
    add(
      'exportOptions.history',
      'history_requires_attachments',
      'Para incluir comparações, anexe o laudo da avaliação correspondente.',
    );
  }
  if (history.mode === 'none' && history.selections.length > 0) {
    add(
      'exportOptions.history.selections',
      'unused_history_selection',
      'Há comparações selecionadas, mas o histórico está desativado.',
      'warning',
    );
  }
  const historySourceIds = new Set<string>();
  history.selections.forEach((selection, index) => {
    const selectionPath = `exportOptions.history.selections.${index}`;
    if (historySourceIds.has(selection.sourceResultId)) {
      add(
        `${selectionPath}.sourceResultId`,
        'duplicate_history_source',
        'Existe mais de uma seleção histórica para a mesma avaliação-fonte.',
      );
    }
    historySourceIds.add(selection.sourceResultId);

    if (!sourceIds.has(selection.sourceResultId)) {
      add(
        `${selectionPath}.sourceResultId`,
        'history_source_not_in_report',
        'A comparação deve partir de uma avaliação-fonte.',
      );
    }
    if (selection.historicalResultIds.length === 0) {
      add(
        `${selectionPath}.historicalResultIds`,
        'empty_history_selection',
        'Selecione ao menos um resultado histórico.',
      );
    }
    if (selection.historicalResultIds.includes(selection.sourceResultId)) {
      add(
        `${selectionPath}.historicalResultIds`,
        'self_comparison',
        'Uma avaliação não pode ser comparada com ela mesma.',
      );
    }
    if (
      form.exportOptions.attachments.mode === 'selected_assessments'
      && !form.exportOptions.attachments.resultIds.includes(selection.sourceResultId)
    ) {
      add(
        `${selectionPath}.sourceResultId`,
        'history_source_not_attached',
        'A avaliação que receberá comparação histórica também deve ser anexada ao PDF.',
      );
    }

    if (history.mode !== 'selected') return;

    const selectedIdSet = new Set(selection.historicalResultIds);
    const snapshotIds = selection.historicalResultSnapshots.map(
      (snapshot) => snapshot.resultId,
    );
    const snapshotIdSet = new Set(snapshotIds);
    const snapshotsMatchSelection =
      snapshotIds.length === snapshotIdSet.size
      && selectedIdSet.size === snapshotIdSet.size
      && [...selectedIdSet].every((resultId) => snapshotIdSet.has(resultId));
    if (!snapshotsMatchSelection) {
      add(
        `${selectionPath}.historicalResultSnapshots`,
        'history_snapshot_review_required',
        'Uma comparação histórica foi salva sem snapshot verificável. Revise e selecione novamente os resultados históricos.',
      );
    }

    const source = sourceById.get(selection.sourceResultId);
    selection.historicalResultSnapshots.forEach((snapshot, snapshotIndex) => {
      const snapshotPath = `${selectionPath}.historicalResultSnapshots.${snapshotIndex}`;
      if (!snapshot.resultId) {
        add(`${snapshotPath}.resultId`, 'required', 'O snapshot histórico deve identificar o resultado.');
      }
      if (!snapshot.sessionId) {
        add(`${snapshotPath}.sessionId`, 'required', 'O snapshot histórico deve identificar a sessão.');
      }
      if (!snapshot.testKey) {
        add(`${snapshotPath}.testKey`, 'required', 'O snapshot histórico deve identificar o instrumento.');
      }
      if (
        snapshot.testKey === ASSESSMENT_REPORT_TEST_KEY
        || snapshot.testKey === LEGACY_COMPOSITE_REPORT_TEST_KEY
      ) {
        add(
          `${snapshotPath}.testKey`,
          'nested_report_history',
          'Um relatório não pode ser usado como resultado histórico.',
        );
      }
      if (snapshot.sourceStatus !== 'completed') {
        add(
          `${snapshotPath}.sourceStatus`,
          'history_not_completed',
          'Somente avaliações históricas concluídas podem compor a comparação.',
        );
      }
      if (!isValidDateValue(snapshot.assessmentDate)) {
        add(`${snapshotPath}.assessmentDate`, 'invalid_date', 'A data do histórico é inválida.');
      }
      if (!isValidDateValue(snapshot.capturedAt)) {
        add(`${snapshotPath}.capturedAt`, 'invalid_date', 'A data do snapshot histórico é inválida.');
      }
      if (
        snapshot.sourceCreatedAt
        && !isValidDateValue(snapshot.sourceCreatedAt)
      ) {
        add(
          `${snapshotPath}.sourceCreatedAt`,
          'invalid_date',
          'A data de criação do histórico é inválida.',
        );
      }
      if (
        snapshot.sourceUpdatedAt
        && !isValidDateValue(snapshot.sourceUpdatedAt)
      ) {
        add(
          `${snapshotPath}.sourceUpdatedAt`,
          'invalid_date',
          'A data de atualização do histórico é inválida.',
        );
      }
      if (!snapshot.sourceUpdatedAt && !snapshot.sourceCreatedAt) {
        add(
          `${snapshotPath}.sourceUpdatedAt`,
          'history_timestamp_required',
          'O resultado histórico não possui timestamp verificável. Revise e selecione-o novamente.',
        );
      }
      if (!snapshot.primaryResult) {
        add(
          `${snapshotPath}.primaryResult`,
          'required',
          'O snapshot histórico deve preservar o resultado principal revisado.',
        );
      }
      if (source && snapshot.testKey !== source.snapshot.testKey) {
        add(
          `${snapshotPath}.testKey`,
          'history_test_mismatch',
          'O resultado histórico deve corresponder ao mesmo instrumento da avaliação-fonte.',
        );
      }
      if (
        source
        && isValidDateValue(snapshot.assessmentDate)
        && isValidDateValue(source.snapshot.assessmentDate)
        && Date.parse(snapshot.assessmentDate) >= Date.parse(source.snapshot.assessmentDate)
      ) {
        add(
          `${snapshotPath}.assessmentDate`,
          'history_not_earlier',
          'A comparação deve usar um resultado anterior à avaliação-fonte.',
        );
      }
    });
  });

  const cbdfIds = new Set<string>();
  form.cbdf.entries.forEach((entry, index) => {
    const path = `cbdf.entries.${index}`;
    if (!entry.id) add(`${path}.id`, 'required', 'A entrada CBDF deve ter um identificador.');
    if (cbdfIds.has(entry.id)) add(`${path}.id`, 'duplicate', 'A entrada CBDF está repetida.');
    cbdfIds.add(entry.id);
    if (!isCbdfChapterCode(entry.chapterCode) || !getCbdfChapter(entry.chapterCode)) {
      add(`${path}.chapterCode`, 'invalid_cbdf_chapter', 'Selecione um dos 36 capítulos-base.');
    }
    if (!entry.professionalStatement) {
      add(
        `${path}.professionalStatement`,
        'required',
        'A descrição CBDF deve ser registrada manualmente pelo profissional.',
      );
    }
    if (!entry.rationale) {
      add(
        `${path}.rationale`,
        'required',
        'Registre a justificativa clínica do diagnóstico fisioterapêutico.',
      );
    }
    if (entry.evidenceResultIds.length === 0) {
      add(
        `${path}.evidenceResultIds`,
        'minimum_evidence',
        'Vincule ao menos uma avaliação-fonte à entrada CBDF.',
      );
    }
    entry.evidenceResultIds.forEach((resultId, evidenceIndex) => {
      if (!sourceIds.has(resultId)) {
        add(
          `${path}.evidenceResultIds.${evidenceIndex}`,
          'evidence_not_in_sources',
          'A evidência CBDF deve corresponder a uma avaliação incluída no relatório.',
        );
      }
    });
    if (!entry.manualCode) {
      add(
        `${path}.manualCode`,
        'full_cbdf_code_required',
        'Transcreva manualmente o código CBDF completo, no formato D04.01.4.4.4.2.',
      );
    } else if (!isCbdfFullCodeForChapter(entry.manualCode, entry.chapterCode)) {
      add(
        `${path}.manualCode`,
        'cbdf_chapter_mismatch',
        'Use capítulo + subcódigo de dois dígitos + quatro subcódigos de um dígito. A validação é apenas estrutural e não confirma a transcrição no catálogo oficial.',
      );
    }
    if (!entry.recordedBy.name) {
      add(`${path}.recordedBy.name`, 'required', 'Identifique quem registrou a entrada CBDF.');
    }
    if (!entry.recordedBy.userId) {
      add(
        `${path}.recordedBy.userId`,
        'professional_user_required',
        'A entrada CBDF deve identificar a conta do fisioterapeuta.',
      );
    }
    if (entry.recordedBy.role !== 'fisioterapeuta') {
      add(
        `${path}.recordedBy.role`,
        'physiotherapist_required',
        'A entrada CBDF deve ser registrada por fisioterapeuta.',
      );
    }
    if (!entry.recordedBy.registration) {
      add(
        `${path}.recordedBy.registration`,
        'professional_registration_required',
        'A entrada CBDF deve identificar o CREFITO do fisioterapeuta.',
      );
    }
    if (!isValidDateValue(entry.recordedAt)) {
      add(`${path}.recordedAt`, 'invalid_date', 'A data do registro CBDF é inválida.');
    }
  });

  const icfGroups: Array<{
    domain: IcfDomain;
    entries: AssessmentReportIcfEntryV2[];
    path: string;
  }> = [
    {
      domain: 'body_functions',
      entries: form.icf.domains.bodyFunctions,
      path: 'icf.domains.bodyFunctions',
    },
    {
      domain: 'body_structures',
      entries: form.icf.domains.bodyStructures,
      path: 'icf.domains.bodyStructures',
    },
    {
      domain: 'activities_and_participation',
      entries: form.icf.domains.activitiesAndParticipation,
      path: 'icf.domains.activitiesAndParticipation',
    },
    {
      domain: 'environmental_factors',
      entries: form.icf.domains.environmentalFactors,
      path: 'icf.domains.environmentalFactors',
    },
  ];
  const icfIds = new Set<string>();
  const icfClassifications = new Set<string>();
  icfGroups.forEach(({ domain, entries, path }) => {
    entries.forEach((entry, index) => {
      const entryPath = `${path}.${index}`;
      if (!entry.id) add(`${entryPath}.id`, 'required', 'A entrada CIF deve ter um identificador.');
      if (icfIds.has(entry.id)) add(`${entryPath}.id`, 'duplicate', 'A entrada CIF está repetida.');
      icfIds.add(entry.id);
      if (!isIcfDomain(entry.domain) || entry.domain !== domain) {
        add(`${entryPath}.domain`, 'icf_domain_mismatch', 'A entrada está no domínio CIF incorreto.');
      }
      if (!isIcfCodeForDomain(entry.code, domain) || !getIcfDomainByCode(entry.code)) {
        add(
          `${entryPath}.code`,
          'invalid_icf_code',
          'O código não corresponde à estrutura e ao componente CIF. Confirme sua existência na fonte oficial.',
        );
      }
      const qualifier = entry.qualifier as IcfQualifier;
      const classificationKey =
        entry.domain === 'activities_and_participation'
          ? `${entry.domain}:${normalizeIcfCode(entry.code)}`
          : `${entry.domain}:${normalizeIcfCode(entry.code)}:${entry.qualifierContext}`;
      if (icfClassifications.has(classificationKey)) {
        add(
          entryPath,
          'duplicate_icf_classification',
          'O mesmo código CIF e tipo de qualificador não devem ser repetidos.',
        );
      }
      icfClassifications.add(classificationKey);
      const validContext =
        domain === 'activities_and_participation'
          ? entry.qualifierContext === 'performance'
          : domain === 'environmental_factors'
            ? entry.qualifierContext === 'barrier' || entry.qualifierContext === 'facilitator'
            : entry.qualifierContext === 'extent';
      if (!validContext) {
        add(
          `${entryPath}.qualifierContext`,
          'invalid_icf_qualifier_context',
          domain === 'activities_and_participation'
            ? 'Em atividades e participação, registre desempenho como primeiro qualificador e capacidade, quando disponível, como segundo.'
            : 'O tipo do qualificador não corresponde ao componente CIF selecionado.',
        );
      }
      if (
        !isIcfProblemQualifier(qualifier)
        && !(domain === 'environmental_factors' && isIcfFacilitatorQualifier(qualifier))
      ) {
        add(
          `${entryPath}.qualifier`,
          'invalid_icf_qualifier',
          'Use 0–4, 8 ou 9; facilitadores +0 a +4 ou +8 somente em fatores ambientais.',
        );
      }
      if (
        domain === 'environmental_factors'
        && (
          (entry.qualifierContext === 'facilitator' && !isIcfFacilitatorQualifier(qualifier))
          || (entry.qualifierContext === 'barrier' && !isIcfProblemQualifier(qualifier))
        )
      ) {
        add(
          `${entryPath}.qualifier`,
          'icf_environmental_sign_mismatch',
          'Barreiras usam 0–4/8/9 e facilitadores usam +0 a +4/+8.',
        );
      }
      if (
        domain === 'activities_and_participation'
        && entry.capacityQualifier !== null
        && !isIcfProblemQualifier(entry.capacityQualifier)
      ) {
        add(
          `${entryPath}.capacityQualifier`,
          'invalid_icf_capacity_qualifier',
          'Use 0–4, 8 ou 9 para o segundo qualificador de capacidade.',
        );
      }
      if (
        domain !== 'activities_and_participation'
        && entry.capacityQualifier !== null
      ) {
        add(
          `${entryPath}.capacityQualifier`,
          'unexpected_icf_capacity_qualifier',
          'O segundo qualificador de capacidade só se aplica a atividades e participação.',
        );
      }
      if (!entry.professionalDescription) {
        add(
          `${entryPath}.professionalDescription`,
          'required',
          'Descreva manualmente o significado funcional desta entrada.',
        );
      }
      if (entry.evidenceResultIds.length === 0) {
        add(
          `${entryPath}.evidenceResultIds`,
          'minimum_evidence',
          'Vincule ao menos uma avaliação-fonte à entrada CIF.',
        );
      }
      entry.evidenceResultIds.forEach((resultId, evidenceIndex) => {
        if (!sourceIds.has(resultId)) {
          add(
            `${entryPath}.evidenceResultIds.${evidenceIndex}`,
            'evidence_not_in_sources',
            'A evidência CIF deve corresponder a uma avaliação incluída no relatório.',
          );
        }
      });
      if (!entry.recordedBy.name) {
        add(`${entryPath}.recordedBy.name`, 'required', 'Identifique quem registrou a entrada CIF.');
      }
      if (!entry.recordedBy.userId) {
        add(
          `${entryPath}.recordedBy.userId`,
          'professional_user_required',
          'A entrada CIF deve identificar a conta do fisioterapeuta.',
        );
      }
      if (entry.recordedBy.role !== 'fisioterapeuta') {
        add(
          `${entryPath}.recordedBy.role`,
          'physiotherapist_required',
          'A entrada CIF do relatório técnico deve ser registrada por fisioterapeuta.',
        );
      }
      if (!entry.recordedBy.registration) {
        add(
          `${entryPath}.recordedBy.registration`,
          'professional_registration_required',
          'A entrada CIF deve identificar o CREFITO do fisioterapeuta.',
        );
      }
      if (!isValidDateValue(entry.recordedAt)) {
        add(`${entryPath}.recordedAt`, 'invalid_date', 'A data do registro CIF é inválida.');
      }
    });
  });

  return issues;
};

export const validateAssessmentReportForm = (
  value: unknown,
): AssessmentReportValidationResult => {
  const form = normalizeAssessmentReportEditorForm(value);
  if (!form) {
    return makeValidationResult([
      {
        path: '',
        code: 'invalid_form_structure',
        message: 'O formulário não corresponde ao contrato do relatório v2.',
        severity: 'error',
      },
    ]);
  }
  return makeValidationResult(validateEditorForm(applyAssessmentReportDescriptiveFallback(form)));
};

export const validateAssessmentReportPayload = (
  value: unknown,
): AssessmentReportValidationResult => {
  const payload = normalizeAssessmentReportPayload(value);
  if (!payload) {
    return makeValidationResult([
      {
        path: '',
        code: 'invalid_payload_structure',
        message: 'O payload não corresponde ao contrato ASSESSMENT_REPORT v2.',
        severity: 'error',
      },
    ]);
  }

  const issues = validateEditorForm(applyAssessmentReportDescriptiveFallback(payload));
  if (!isValidDateValue(payload.generatedAt)) {
    issues.push({
      path: 'generatedAt',
      code: 'invalid_date',
      message: 'A data de geração é inválida.',
      severity: 'error',
    });
  }
  if (!isValidDateValue(payload.updatedAt)) {
    issues.push({
      path: 'updatedAt',
      code: 'invalid_date',
      message: 'A data de atualização é inválida.',
      severity: 'error',
    });
  }
  if (
    isValidDateValue(payload.generatedAt)
    && isValidDateValue(payload.updatedAt)
    && Date.parse(payload.updatedAt) < Date.parse(payload.generatedAt)
  ) {
    issues.push({
      path: 'updatedAt',
      code: 'before_generation',
      message: 'A atualização não pode anteceder a geração.',
      severity: 'error',
    });
  }
  return makeValidationResult(issues);
};

export const countAssessmentReportFindings = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): AssessmentReportFindingCounts => {
  const byStatus: Record<AssessmentReportFindingStatus, number> = {
    within_expected: 0,
    borderline: 0,
    altered: 0,
    not_classified: 0,
    not_interpretable: 0,
  };
  sources.forEach((source) => {
    byStatus[source.finding.status] += 1;
  });
  return {
    total: sources.length,
    interpretable:
      byStatus.within_expected + byStatus.borderline + byStatus.altered,
    withinExpected: byStatus.within_expected,
    borderline: byStatus.borderline,
    altered: byStatus.altered,
    notClassified: byStatus.not_classified,
    notInterpretable: byStatus.not_interpretable,
    byStatus,
  };
};

/** Preserva a síntese autoral sem acrescentar conclusões ou rótulos automáticos. */
export const buildAssessmentReportContinuousNarrative = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
  professionalOverview: string,
): string => {
  void sources;
  return professionalOverview.trim();
};

const toCalendarDate = (value: string): string => {
  const leadingDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/)?.[0];
  if (leadingDate) return leadingDate;
  const date = new Date(value);
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

const formatCalendarDatePtBr = (value: string): string => {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

export const getAssessmentReportPeriod = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): AssessmentReportPeriod | null => {
  const validDates = sources
    .map((source) => source.snapshot.assessmentDate)
    .filter(isValidDateValue)
    .map(toCalendarDate)
    .sort();
  if (validDates.length === 0) return null;

  const startDate = validDates[0];
  const endDate = validDates[validDates.length - 1];
  const isSingleDay = startDate === endDate;
  return {
    startDate,
    endDate,
    isSingleDay,
    sourceCount: validDates.length,
    label: isSingleDay
      ? formatCalendarDatePtBr(startDate)
      : `${formatCalendarDatePtBr(startDate)} a ${formatCalendarDatePtBr(endDate)}`,
  };
};
